'use client';

// Global
import { useEffect, useRef } from 'react';
import type { ComponentParams } from '@sitecore-content-sdk/nextjs';
import { tv } from 'tailwind-variants';

// Local
import { useScrollElementIntoView } from 'lib/hooks/useScrollElementIntoView';
import { getTestProps } from 'lib/testing/utils';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import useIsEditing from 'lib/hooks/useIsEditing';
import { useSetIsDataAvailable } from 'lib/context/IsDataAvailableContext';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import { createCTAClickGTMEvent } from 'lib/utils/gtm-utils';
import useDictionary from 'lib/hooks/useDictionary';
import type { RelatedExpertCommentsNewsItem } from 'lib/search/fetch-related-expert-comments';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';

export type RelatedExpertCommentsWidgetProps = {
  params?: ComponentParams;
  uid?: string;
  id?: string;
  staticProps?: {
    initialNews: RelatedExpertCommentsNewsItem[];
    fetchError: string | null;
  };
};

const RelatedExpertCommentsWidget = ({
  params,
  uid,
  id,
  staticProps,
}: RelatedExpertCommentsWidgetProps) => {
  const { getDictionaryValue } = useDictionary();
  const articleCardsRef = useRef<HTMLDivElement>(null);

  const news = staticProps?.initialNews ?? [];
  const error = staticProps?.fetchError ?? null;
  const isEditing = useIsEditing();
  const setIsDataAvailable = useSetIsDataAvailable();

  useEffect(() => {
    const available = !isEditing && news.length > 0;
    setIsDataAvailable(available);
  }, [isEditing, news.length, setIsDataAvailable]);

  useScrollElementIntoView(articleCardsRef.current, {
    scrollTargetId: 'search-result-cards',
  });

  const { RenderingIdentifier } = params || {};

  const {
    newsList,
    newsListItem,
    container,
    header,
    body,
    contentWrapper,
    content,
    headingWithEyebrow,
    eyebrowCategory: eyebrowCategoryTV,
    heading: headingTV,
    shortDescription: shortDescriptionTV,
    sectionDivider,
    footer,
    cta,
    imageWrapper,
  } = TAILWIND_VARIANTS({
    colCount: '3',
  });

  if (!isEditing && (!news || news.length === 0) && !error) {
    return <></>;
  }

  return (
    <section
      ref={articleCardsRef}
      id={id}
      data-component="authorable/shared/search/ExpertCommentsOnNews"
      {...editingHydrationProps(isEditing)}
      {...getTestProps(`component-search-result-${uid}`)}
    >
      <div id={RenderingIdentifier} {...getTestProps(`search-result-cards`)}>
        {!error && news?.length > 0 && (
          <ul className={newsList()}>
            {news.map((n, idx) => (
              <li key={n?.id || idx} className={newsListItem()}>
                <article
                  className={container()}
                  data-component="authorable/shared/lists/carditem"
                  {...editingHydrationProps(isEditing)}
                  id={RenderingIdentifier}
                  {...getTestProps(`card-item-${n?.id}`)}
                >
                  <div className={header()}>
                    {n?.image_url ? (
                      <ImageWrapper
                        field={{
                          value: {
                            src: n?.image_url,
                            alt: n?.name,
                            title: n?.name,
                          },
                        }}
                        className={imageWrapper()}
                        {...getTestProps(`image`)}
                      />
                    ) : (
                        <ImageWrapper
                          field={{
                            value: {
                              src: '/assets/profile_placeholder.png',
                              alt: 'No image',
                            },
                          }}
                          className={imageWrapper()}
                          {...getTestProps(`related-profile-image-placeholder`)}
                        />
                    )}
                  </div>
                  <div className={body()}>
                    <div className={contentWrapper()}>
                      <div className={content()}>
                        <div className={headingWithEyebrow()}>
                          <PlainTextWrapper
                            className={eyebrowCategoryTV()}
                            editable={false}
                            field={{ value: n?.type }}
                            tag="div"
                            {...getTestProps(`category`)}
                          />
                          <PlainTextWrapper
                            className={headingTV()}
                            field={{ value: n?.name }}
                            {...getTestProps(`heading`)}
                          />
                        </div>

                        <RichTextWrapper
                          className={shortDescriptionTV()}
                          field={{ value: n?.description }}
                          {...getTestProps(`short-description`)}
                        />
                        <div className={sectionDivider()} />
                      </div>
                      <div className={footer()}>
                        {n?.url && (
                          <LinkWrapper
                            ctaComponentClass="component-card-item-button-1-color"
                            className={cta()}
                            ctaVariant="secondary"
                            field={{
                              value: {
                                href: n?.url,
                                text: getDictionaryValue('RelatedArticlesCTAText') || 'Read more',
                                linktype: 'internal',
                              },
                            }}
                            ctaIcon="arrow-right"
                            {...getTestProps(`secondary-cta`)}
                            gtmEvent={createCTAClickGTMEvent({
                              header: n?.name,
                              ctaText: getDictionaryValue('RelatedArticlesCTAText') || 'Read more',
                              ctaType: 'blue',
                              componentName: 'Card Set',
                              ctaLink: n?.url,
                            })}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </article>
              </li>
            ))}
          </ul>
        )}
        {error && (
          <ul className={newsList()}>
            <li className={newsListItem()}>
              <span>{error}</span>
            </li>
          </ul>
        )}
      </div>
    </section>
  );
};

export default RelatedExpertCommentsWidget;

const TAILWIND_VARIANTS = tv({
  slots: {
    newsList: ['gap-6', 'grid', 'grid-cols-1'],
    newsListItem: ['flex', 'flex-col'],
    container: [
      'flex',
      'w-full',
      'overflow-hidden',
      'rounded-general-border-radius-card',
      'bg-component-card-item-surface',
      'shadow-color-black-alpha-10',
      'shadow-[0_0_8px_0]',
    ],
    header: [
      'relative',
      'min-h-max',
      'w-full',
      'h-full md:h-auto',
      'overflow-hidden',
      'aspect-[1.61/1]',
      'text-color-fill-brand-1',
      'transition',
      'duration-300',
      'ease-in-out',
      'group',
      'pt-component-article-card-portrait-image-padding-top',
      'px-component-article-card-portrait-image-padding-x',
      'pb-component-article-card-portrait-image-padding-bottom',
    ],
    body: ['flex', 'flex-col', 'w-full', 'items-center'],
    contentWrapper: [
      'flex',
      'flex-col',
      'w-full',
      'py-component-card-item-landscape-content-1-col-padding-y',
      'px-component-card-item-landscape-content-1-col-padding-x',
    ],
    content: ['flex', 'flex-col', 'w-full', 'gap-spacing-spacing-24'],
    headingWithEyebrow: ['flex', 'flex-col', 'w-full', 'gap-spacing-spacing-4'],
    eyebrow: [
      'text-component-card-item-eyebrow',
      'font-typography-font-family-body',
      'text-typography-font-size-body-xSmall',
      'mb-spacing-spacing-4',
      'font-semibold',
      'font-normal',
      'leading-[150%]',
    ],
    eyebrowCategory: [
      'text-component-card-item-eyebrow',
      'font-typography-font-family-body',
      'text-typography-font-size-body-xSmall',
      'mb-spacing-spacing-4',
      'font-semibold',
      'font-normal',
      'leading-[150%]',
      'uppercase',
    ],
    heading: [
      'font-typography-font-family-header',
      'font-normal',
      'text-typography-font-size-medium',
      'text-component-card-item-title',
      'leading-typography-line-height-medium',
    ],
    subheading: [
      'font-semibold',
      'font-typography-font-family-body',
      'text-typography-font-size-body-small',
      'leading-[150%]',
      'text-component-card-item-subtitle',
    ],
    description: [
      'font-typography-font-family-body',
      'font-typography-font-weight-body',
      'text-typography-font-size-body-medium',
      'text-component-card-item-body',
      'leading-typography-line-height-body-medium',
      'mb-spacing-spacing-8',
    ],
    shortDescription: [
      'font-typography-font-family-body',
      'font-typography-font-weight-body',
      'text-typography-font-size-body-medium',
      'text-component-card-item-body',
      'leading-typography-line-height-body-medium',
    ],
    sectionDivider: [
      'w-component-section-divider-width',
      'h-component-section-divider-height',
      'bg-component-section-divider',
    ],
    footer: [
      'flex',
      'flex-wrap',
      'flex-col',
      'gap-spacing-spacing-8',
      'items-start',
      'justify-center',
      'w-full',
    ],
    cta: [
      'flex',
      'gap-spacing-spacing-8',
      'items-center',
      'justify-center',
      'py-spacing-spacing-16',
      'px-spacing-spacing-24',
      '!w-auto',
    ],
    imageWrapper: ['w-full', 'h-full', 'object-cover'],
  },
  variants: {
    colCount: {
      '1': {
        newsList: ['lg:grid-cols-1'],
        container: ['h-full', 'flex-col', 'md:flex-row', 'md:gap-0'],
        body: ['flex-row', 'items-start', 'text-left', 'lg:pl-0', 'justify-center'],
        description: [],
        content: ['text-left', 'justify-center'],
        contentWrapper: ['text-left', 'justify-center', 'gap-spacing-spacing-24'],
        header: [
          'sm:py-component-article-card-landscape-image-padding-y',
          'sm:px-component-article-card-landscape-image-padding-x',
          'sm:pr-0',
        ],
        imageWrapper: ['aspect-[1.61/1]'],
      },
      '2': {
        newsList: ['md:grid-cols-2'],
        body: ['flex-row', 'items-start', 'text-left', 'h-auto', 'md:h-full', 'md:justify-between'],
        container: ['h-full', 'flex-col', 'lg:flex-row'],
        description: [],
        content: ['text-left', 'md:flex-1', 'md:h-full', 'md:min-h-0'],
        contentWrapper: [
          'text-left',
          'h-auto',
          'md:h-full',
          'justify-start md:justify-between',
          'md:min-h-0',
          'py-component-card-item-landscape-content-padding-y',
          'px-component-card-item-landscape-content-padding-x',
          'gap-spacing-spacing-24',
        ],
        header: [
          'aspect-[1.61/1] md:aspect-[16/9] lg:aspect-[5/4] md:min-h-[304px]',
          'sm:py-component-article-card-landscape-image-padding-y',
          'sm:px-component-article-card-landscape-image-padding-x',
          'sm:pr-0',
          'rounded-r-none md:rounded-b-none lg:rounded-r-none',
        ],
        imageWrapper: [
          'rounded-r-none',
          'md:rounded-b-none',
          'lg:rounded-r-none',
          'aspect-[1.61/1] md:aspect-[16/9] lg:aspect-[5/4]',
        ],
      },
      '3': {
        newsList: ['lg:grid-cols-3'],
        header: ['aspect-[1.61/1]'],
        imageWrapper: [],
        container: ['h-full', 'flex-col', 'md:flex-row', 'lg:flex-col'],
        body: ['flex-col', 'h-full', 'items-start', 'text-left'],
        content: ['text-left'],
        contentWrapper: ['h-full', 'text-left', 'gap-spacing-spacing-24'],
        footer: ['mt-auto'],
      },
      '4': {
        newsList: ['lg:grid-cols-4'],
        container: ['h-full', 'flex-col'],
        body: ['flex-col', 'items-center', 'text-left'],
        content: ['text-left'],
        contentWrapper: ['h-full', 'text-left', 'gap-spacing-spacing-24'],
        footer: ['mt-auto'],
        imageWrapper: ['aspect-[1.61/1]'],
      },
    },
  },
  defaultVariants: {
    style: 'primary',
    colCount: '3',
  },
});
